import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Header from "../components/Header";
import Breadcrumb from "../components/Breadcrumb";
import LeagueContextActions from "../components/LeagueContextActions";
import TradeHub from "../components/TradeHub";
import ProviderBetaBanner from "../components/ProviderBetaBanner";
import WeekProgressBar from "../components/WeekProgressBar";
import { safeFetch } from "../utils/api";
import usePageMeta from "../utils/usePageMeta";
import { setClarityTags, trackClarityEvent } from "../utils/clarity";

// Loading stages, in order. The Trade Hub analysis needs every weekly lineup
// from the first trade onward, which can take minutes on a cold cache, so the
// page drives that work itself and shows what it is waiting on.
const STAGES = {
    TRADES: "trades", // fetching the trade list (fast, cached per season)
    WEEKS: "weeks", // hydrating weekly lineups one request per week
    SCORING: "scoring", // final analysis request over the stored weeks
    DONE: "done",
};

// How many week requests to keep in flight. The server-side snapshot store
// coordinates concurrent requests, and each week is bounded by one provider
// fetch, so a small pool cuts wall time without hammering the provider API.
const WEEK_CONCURRENCY = 3;

const runWithConcurrency = async (items, limit, worker) => {
    let index = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (index < items.length) {
            const item = items[index];
            index += 1;
            await worker(item);
        }
    });
    await Promise.all(runners);
};

const LoadingStatus = ({ stage, preview, progress }) => {
    if (stage === STAGES.DONE) return null;

    const tradeCount = preview?.trades?.length ?? 0;
    const requiredWeeks = preview?.required_weeks || [];
    const cachedCount = requiredWeeks.length - (progress?.total ?? requiredWeeks.length);

    let title = "Finding trades";
    let detail = "Checking this season's accepted trades.";
    if (stage === STAGES.WEEKS) {
        title = "Loading weekly lineups";
        detail = `Trade impact needs every starting lineup from week ${requiredWeeks[0]} through week ${
            requiredWeeks[requiredWeeks.length - 1]
        }.${cachedCount > 0 ? ` ${cachedCount} ${cachedCount === 1 ? "week is" : "weeks are"} already stored.` : ""}`;
    } else if (stage === STAGES.SCORING) {
        title = "Scoring trade impact";
        detail = `Comparing actual lineups against projection-based alternatives for ${tradeCount} ${
            tradeCount === 1 ? "trade" : "trades"
        }. This usually takes a few seconds.`;
    }

    return (
        <div
            className="rounded-lg border border-border bg-surface px-5 py-4 space-y-3"
            role="status"
            aria-live="polite"
        >
            <div className="flex items-start gap-3">
                <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-forest" />
                <div>
                    <p className="font-sans text-sm font-semibold text-ds-text">{title}</p>
                    <p className="mt-0.5 font-sans text-xs font-semibold text-text-muted">{detail}</p>
                </div>
            </div>
            {stage === STAGES.WEEKS && progress && (
                <WeekProgressBar
                    loaded={progress.loaded}
                    total={progress.total}
                    weeks={progress.weeks}
                    caption={
                        progress.loaded < progress.total
                            ? `${progress.loaded} of ${progress.total} weeks loaded`
                            : "All weeks loaded — starting analysis…"
                    }
                />
            )}
        </div>
    );
};

const LeagueTradeHubPage = () => {
    const { provider, leagueYear, leagueId } = useParams();
    const navigate = useNavigate();
    const [leagueData, setLeagueData] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [stage, setStage] = useState(STAGES.TRADES);
    const [progress, setProgress] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    usePageMeta({
        title: leagueData
            ? `${leagueData.league_name} Trade Hub (${leagueYear})`
            : `Trade Hub — ${leagueYear}`,
        description: leagueData
            ? `Trade impact timeline for ${leagueData.league_name} (${leagueYear}) — every accepted trade, the players involved, and the matchups they influenced.`
            : undefined,
        canonical: `/fantasy_stats/trade-hub/${provider}/${leagueYear}/${leagueId}/`,
    });

    if (fetchError) {
        throw fetchError;
    }

    useEffect(() => {
        setClarityTags({
            feature_surface: "trade_hub",
            has_league_context: "true",
            provider,
            league_year: String(leagueYear),
        });
    }, [leagueYear, provider]);

    useEffect(() => {
        if (!leagueData) return;
        trackClarityEvent("league_trade_hub_viewed");
    }, [leagueData]);

    // Fetch league metadata for hero band
    useEffect(() => {
        safeFetch(`/api/league/${provider}/${leagueYear}/${leagueId}/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    setLeagueData(data);
                }
            })
            .catch((err) => {
                console.error("ERROR: /api/league/:provider/:year/:id", leagueYear, leagueId, err);
                setFetchError(err);
            });
    }, [provider, leagueYear, leagueId, navigate]);

    // Staged Trade Hub load: trades → weekly lineups → analysis.
    useEffect(() => {
        if (!leagueYear || !leagueId) return;
        let cancelled = false;
        const base = `/api/trade-hub/${provider}/${leagueYear}/${leagueId}/`;

        setPreview(null);
        setResult(null);
        setProgress(null);
        setStage(STAGES.TRADES);

        const load = async () => {
            const previewData = await safeFetch(`${base}trades/`, {}, false, 2);
            if (cancelled) return;
            if (previewData?.redirect) {
                navigate(previewData.redirect);
                return;
            }
            setPreview(previewData);

            const trades = Array.isArray(previewData?.trades) ? previewData.trades : [];
            if (!trades.length) {
                // Nothing to analyze; the preview is the final answer.
                setResult(previewData);
                setStage(STAGES.DONE);
                return;
            }

            if (!previewData.ready) {
                const cached = new Set(previewData.cached_weeks || []);
                const weeks = (previewData.required_weeks || []).filter((week) => !cached.has(week));
                if (weeks.length) {
                    setProgress({ loaded: 0, total: weeks.length, weeks });
                    setStage(STAGES.WEEKS);
                    await runWithConcurrency(weeks, WEEK_CONCURRENCY, async (week) => {
                        if (cancelled) return;
                        try {
                            await safeFetch(
                                `/api/prefetch-season-data/${provider}/${leagueYear}/${leagueId}/${week}/?projections=1`,
                                {},
                                false,
                                0
                            );
                        } catch (err) {
                            // The final analysis request will retry this week itself.
                            console.warn(`Trade Hub prefetch failed for week ${week}:`, err);
                        }
                        if (!cancelled) {
                            setProgress((prev) => (prev ? { ...prev, loaded: prev.loaded + 1 } : prev));
                        }
                    });
                    if (cancelled) return;
                }
            }

            setStage(STAGES.SCORING);
            const full = await safeFetch(base, {}, false, 2);
            if (cancelled) return;
            if (full?.redirect) {
                navigate(full.redirect);
                return;
            }
            setResult(full);
            setStage(STAGES.DONE);
        };

        load().catch((err) => {
            if (cancelled) return;
            console.error("ERROR: /api/trade-hub/:provider/:year/:id", leagueYear, leagueId, err);
            setFetchError(err);
        });

        return () => {
            cancelled = true;
        };
    }, [provider, leagueYear, leagueId, navigate]);

    const leagueUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}/`;
    const displayData = result || preview;

    return (
        <div className="min-h-screen bg-bg">
            <Header />

            {/* Hero Band */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-5xl mx-auto px-6 py-6">
                    <Breadcrumb
                        items={[
                            { label: "Dashboard", to: "/dashboard" },
                            {
                                label: leagueData?.league_name ?? "…",
                                to: leagueData ? leagueUrl : undefined,
                            },
                            { label: "Trade Hub" },
                        ]}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                {leagueYear} Trade Hub
                            </p>
                            {leagueData ? (
                                <>
                                    <h1 className="font-display text-4xl font-black text-white leading-none">
                                        {leagueData.league_name}
                                    </h1>
                                    <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                        League ID: {leagueData.league_id}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="h-9 w-64 bg-white/20 rounded animate-pulse mb-2" />
                                    <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
                                </>
                            )}
                        </div>
                        <LeagueContextActions
                            league={leagueData}
                            provider={provider}
                            leagueYear={leagueYear}
                            leagueId={leagueId}
                            activePage="tradehub"
                        />
                    </div>
                </div>
            </div>

            {/* Trade Hub Content */}
            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
                <ProviderBetaBanner
                    provider={provider}
                    leagueId={leagueId}
                    leagueYear={leagueYear}
                    leagueName={leagueData?.league_name}
                />
                <LoadingStatus stage={stage} preview={preview} progress={progress} />
                {/* The status panel is the only loading signal until the trade
                    list arrives; the real cards then render in pending mode. */}
                {displayData && (
                    <div className="rounded-[1.75rem] border border-border/80 bg-white px-4 py-5 shadow-[0_18px_45px_rgba(31,56,41,0.06)] sm:px-6 sm:py-6">
                        <TradeHub data={displayData} pending={stage !== STAGES.DONE} />
                    </div>
                )}
            </main>
        </div>
    );
};

export default LeagueTradeHubPage;
